import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import MainBody from "../../../components/MainBody/MainBody";
import CustomTooltip from "../../../components/CustomTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatName } from "../../../lib/Misc";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { CalendarDatePicker } from "../../../components/CalendarDatePicker";
import { AnimatedTooltip } from "../../../components/AnimatedTooltip";
import Confirmation from "../../../components/Confirmation";
import { Button } from "../../../components/ui/button";

const ArchivedTransactions = ({
  selectedDateRange,
  setSelectedDateRange,
  trustId,
  isDesktop,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [archivedTransactionData, setArchivedTransactionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [deletedItem, setDeletedItem] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [showDeleteAll, setShowDeleteAll] = useState(false);

  // *API Call for Fetching Archived Transaction List - Start
  const fetchArchivedTransactions = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.BANK_TRANSACTION_LIST}?trust_id=${
            trustId ? trustId : ""
          }&deleted=1&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&start_date=${
            moment(selectedDateRange?.from).format("DD-MM-YYYY")
              ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
              : ""
          }&end_date=${
            moment(selectedDateRange?.to).format("DD-MM-YYYY")
              ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
              : ""
          }`
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setArchivedTransactionData(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDateRange?.from, selectedDateRange?.to, trustId]
  );

  useEffect(() => {
    fetchArchivedTransactions();
  }, [fetchArchivedTransactions]);
  // *API Call for Bank Transaction Batches List - End

  useEffect(() => {
    navigate(
      `?tab=audit-trail&childTab=import-history&limit=${
        limit ? limit : defaultLimitPagination
      }&page=${page ? page : 1}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, page]);

  // *Archived Transaction List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - Start
  const archivedTransactionGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: archivedTransactionData[i]?.id,
        date: archivedTransactionData[i]?.transaction_date
          ? moment(
              archivedTransactionData[i]?.transaction_date,
              "DD-MM-YYYY"
            ).format("DD-MM-YYYY")
          : "-",
        project_name: archivedTransactionData[i]?.builder_project?.project_name
          ? archivedTransactionData[i]?.builder_project?.project_name
          : "-",
        description: archivedTransactionData[i]?.description
          ? archivedTransactionData[i]?.description
          : "-",
        credit:
          archivedTransactionData[i]?.base_type === 1 ? (
            <span className="rag-green">
              $
              {archivedTransactionData[i]?.amount?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ) : (
            "-"
          ),
        debit:
          archivedTransactionData[i]?.base_type === 0 ? (
            <span className="rag-red">
              $
              {archivedTransactionData[i]?.amount?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ) : (
            "-"
          ),
        bank_transaction_source: archivedTransactionData[i]?.source
          ? archivedTransactionData[i]?.source
          : "-",
        archived_by: archivedTransactionData[i]?.user
          ? formatName(
              archivedTransactionData[i]?.user?.first_name,
              archivedTransactionData[i]?.user?.last_name
            )
          : "-",
        action: (
          <div style={{ textAlign: "center" }}>
            <CustomTooltip message={"Delete"}>
              <span>
                <span>
                  <FontAwesomeIcon
                    onClick={() =>
                      toggleDeleteModal(archivedTransactionData[i]?.id)
                    }
                    icon={faTrash}
                    size="1x"
                  />
                </span>
              </span>
            </CustomTooltip>
          </div>
        ),
      });
    }
    return items;
  };

  const archivedTransactions = archivedTransactionGenerator(
    archivedTransactionData?.length
  );

  const arrayRemove = (arr, value) => {
    return arr.filter(function (ele) {
      return ele !== value;
    });
  };

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    onSelectAll: (row, isSelect) => {
      if (archivedTransactionData?.length > 0) {
        if (row) {
          const ids =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect?.map((item) => {
              return item?.key;
            });
          setSelectedRowIds(selectedRowIds ? selectedRowIds.concat(ids) : ids);
        } else {
          const removableData =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect.map(function (data) {
              return data?.key;
            });
          setSelectedRowIds(
            selectedRowIds
              ? selectedRowIds.filter((item) => !removableData.includes(item))
              : []
          );
        }
      }
    },
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedRowIds((keys) => [...keys, row?.key]);
      } else {
        setSelectedRowIds(arrayRemove(selectedRowIds, row?.key));
      }
    },
  };
  // *Archived Transaction List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - End

  // *API Call for Archived Transaction Permanent Delete - Start
  const toggleDeleteModal = (id) => {
    setShowDelete(!showDelete);
    setDeletedItem(id);
  };

  const toggleDeleteAllModal = () => {
    setShowDeleteAll(!showDeleteAll);
  };

  const handleDeleteTransaction = async () => {
    if (deletedItem) {
      const formData = new FormData();
      formData.append(`ids[0]`, deletedItem);
      formData.append("trust_id", trustId);
      await fileUpload(`${API.PERMANETALY_DELETE_BANK_STATEMENT}`, formData)
        .then((response) => {
          if (response?.status === 200) {
            fetchArchivedTransactions(limit, page);
            setTimeout(() => {
              setDeletedItem("");
              setShowDelete(!showDelete);
            }, 700);
          }
        })
        .catch((e) => {});
    }
  };

  const handleDeleteAll = async () => {
    const formData = new FormData();
    if (selectedRowIds?.length > 0) {
      for (let i = 0; i < selectedRowIds?.length; i++) {
        formData.append(`ids[${i}]`, selectedRowIds[i]);
      }
    }
    formData.append("trust_id", trustId);
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.PERMANETALY_DELETE_BANK_STATEMENT}`,
        formData
      );
      setLoading(false);
      if (data) {
        setSelectedRowIds([]);
        toggleDeleteAllModal();
        fetchArchivedTransactions(limit, page);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "date",
      text: "Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "credit",
      text: "Credit",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "debit",
      text: "Debit",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "archived_by",
      text: "Archived By",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "bank_transaction_source",
      text: "Bank Transaction Source",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchArchivedTransactions(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchArchivedTransactions(limit, page);
  };

  return (
    <>
      <div className="relative">
        <MainBody
          search={false}
          innerBody={true}
          actionAlign={"end"}
          parentHeight={true}
          actionContent={
            <>
              {" "}
              <div className="flex items-center gap-2">
                <CalendarDatePicker
                  date={selectedDateRange}
                  onDateSelect={setSelectedDateRange}
                  variant="outline"
                  align={isDesktop ? "end" : "start"}
                  disabledDate={(date) => date > new Date()}
                />
                <AnimatedTooltip title={"Delete All"}>
                  <Button onClick={() => toggleDeleteAllModal()}>
                    Delete All
                  </Button>
                </AnimatedTooltip>
              </div>
            </>
          }
          loading={loading}
        >
          <div className="custom-table">
            <div className="table-body-class max-h-[560px]">
              <BootstrapTable
                keyField="key"
                remote
                data={archivedTransactions}
                selectRow={selectRow}
                columns={columns}
                rowClasses={TRClasses}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>

            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={handleUpdateLimit}
              updatePage={handleChangePage}
              from={from}
              to={to}
            />

            {showDelete && (
              <Confirmation
                title={"Delete Bank Transaction"}
                open={showDelete}
                setOpen={setShowDelete}
                toggleShow={() => {
                  toggleDeleteModal();
                }}
                className="sm:max-w-fit"
                message={`Are your sure you want to delete this transaction permanently?`}
                okAction={async () => {
                  await handleDeleteTransaction();
                }}
                popupType={"bank-transactions"}
                loading={loading}
                cancelAction={() => {
                  toggleDeleteModal();
                }}
              />
            )}

            {showDeleteAll && (
              <Confirmation
                title={"Delete all Bank Transactions"}
                open={showDeleteAll}
                setOpen={setShowDeleteAll}
                toggleShow={() => {
                  toggleDeleteAllModal();
                }}
                className="sm:max-w-fit"
                message={`Are you sure you want to delete
                ${selectedRowIds?.length === 0 ? "all" : "selected"} bank
                transactions permanently?`}
                okAction={async () => {
                  await handleDeleteAll();
                }}
                popupType={"bank-transactions"}
                loading={loading}
                cancelAction={() => {
                  toggleDeleteAllModal();
                }}
              />
            )}
          </div>
        </MainBody>
      </div>
    </>
  );
};

export default ArchivedTransactions;
